import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { createPost, createDraft, preparePostData, isChapterType, isSceneType } from "../services/postService";
import { updateDraft, fetchDraftById } from "../services/draftService";
import { usePostReducer } from "../hooks/usePostReducer";
import Sidebar from "../utils/unifiedSidebar";
import TopNavigation from "../components/Post-Creation/TopNavigation";
import ContentTypeSelector from "../components/Post-Creation/ContentTypeSelector";
import PostForm from "../components/Post-Creation/PostForm";
import DraftModal from "../components/Modal/DraftModal";
import DraftActionModal from "../components/Modal/DraftActionModal";
import ErrorModal from "../components/Modal/ErrorModal";

const CreatePost = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showDraftActionModal, setShowDraftActionModal] = useState(false);
  const [formError, setFormError] = useState("");
  const { state, actions, helpers } = usePostReducer();
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const contentRef = useRef(null);

  const handleSelectDraftForEdit = async (draftId) => {
    try {
      const draft = await fetchDraftById(apiCall, draftId);
      actions.loadDraft(draft);
      setFormError("");
      setShowDraftModal(false);
    } catch (error) {
      setFormError(error.message || "Failed to load draft");
    }
  };

  // Validates current form state and returns { postData } or { error }.
  const buildPostData = () => {
    const isChapter = isChapterType(state.postType);
    const isScene = isSceneType(state.postType);

    if (isChapter) {
      const chapters = [];
      for (let i = 0; i < state.chapterTitles.length; i++) {
        if (state.chapterTitles[i]?.trim() && state.chapterContents[i]?.trim()) {
          chapters.push({
            chapterNumber: i + 1,
            title: state.chapterTitles[i].trim(),
            content: state.chapterContents[i].trim(),
          });
        }
      }
      if (chapters.length === 0) {
        return { error: "Please add at least one chapter with title and content" };
      }
      return {
        postData: {
          title: state.title.trim(),
          type: state.postType,
          chapters,
          tags: state.tags,
          backgroundStyle: state.backgroundOption,
          fontStyle: state.selectedFont,
          colorStyle: state.selectedColor,
        },
      };
    }

    if (isScene) {
      const scenes = [];
      for (let i = 0; i < state.sceneTitles.length; i++) {
        if (state.sceneTitles[i]?.trim() && state.sceneContents[i]?.trim()) {
          scenes.push({
            sceneNumber: i + 1,
            title: state.sceneTitles[i].trim(),
            content: state.sceneContents[i].trim(),
          });
        }
      }
      if (scenes.length === 0) {
        return { error: "Please add at least one scene with title and content" };
      }
      return {
        postData: {
          title: state.title.trim(),
          type: state.postType,
          scenes,
          tags: state.tags,
          backgroundStyle: state.backgroundOption,
          fontStyle: state.selectedFont,
          colorStyle: state.selectedColor,
        },
      };
    }

    if (!state.postType || !state.title.trim() || !state.content.trim()) {
      return { error: "Please fill in all required fields" };
    }

    return { postData: preparePostData(state) };
  };

  // Publishes the post (used by both "Share Now" in ShareOptionsModal
  // and "Share Now" in DraftActionModal — always means the same thing).
  const handleShareNow = async () => {
    const { postData, error } = buildPostData();
    if (error) {
      setFormError(error);
      return;
    }

    // If a draft is loaded, include its ID so the backend can delete
    // the draft (and its reviews) atomically after the post is created.
    const payload = state.loadedDraftId
      ? { ...postData, draftId: state.loadedDraftId }
      : postData;

    setFormError("");
    actions.setLoading(true);
    try {
      const result = await createPost(payload, apiCall);
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["myDrafts"] });
        setShowDraftActionModal(false);
        actions.resetForm();
        navigate("/feed?type=for_you", { replace: true });
      } else {
        setFormError(result.error || "Failed to create post");
      }
    } catch (err) {
      setFormError(err.message || "Failed to create post");
    } finally {
      actions.setLoading(false);
    }
  };

  // "Share Draft for Review" — only reachable from ShareOptionsModal,
  // which only shows when no draft is loaded. Always creates a new draft.
  const handleSaveDraft = async () => {
    const { postData, error } = buildPostData();
    if (error) {
      setFormError(error);
      return;
    }

    setFormError("");
    actions.setLoading(true);
    try {
      const result = await createDraft(postData, apiCall);
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["myDrafts"] });
        actions.resetForm();
        navigate("/feed?type=for_you", { replace: true });
      } else {
        setFormError(result.error || "Failed to save draft");
      }
    } catch (err) {
      setFormError(err.message || "Failed to save draft");
    } finally {
      actions.setLoading(false);
    }
  };

  // "Update Draft" — only reachable from DraftActionModal, which only
  // shows when a draft is loaded. Updates that same draft in place.
  const handleUpdateExistingDraft = async () => {
    const { postData, error } = buildPostData();
    if (error) {
      setFormError(error);
      return;
    }

    setFormError("");
    actions.setLoading(true);
    try {
      const result = await updateDraft(state.loadedDraftId, postData, apiCall);
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["myDrafts"] });
        setShowDraftActionModal(false);
        actions.resetForm();
        navigate("/feed?type=for_you", { replace: true });
      } else {
        setFormError(result.error || "Failed to update draft");
      }
    } catch (err) {
      setFormError(err.message || "Failed to update draft");
    } finally {
      actions.setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="min-[1285px]:ml-[15.4rem] min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 min-w-0">
        <TopNavigation
          onSubmit={handleShareNow}
          onSaveDraft={handleSaveDraft}
          hasLoadedDraft={!!state.loadedDraftId}
          onOpenDraftAction={() => setShowDraftActionModal(true)}
          loading={state.loading}
          postType={state.postType}
          title={state.title}
          content={state.content}
          chapterTitles={state.chapterTitles}
          chapterContents={state.chapterContents}
          sceneTitles={state.sceneTitles}
          sceneContents={state.sceneContents}
        />

        <div className="max-w-4xl mx-auto sm:p-4 mt-6">
          <ContentTypeSelector
            postType={state.postType}
            setPostType={actions.setPostType}
          />

          {/* {formError && (
            <div className="mx-4 sm:mx-0 mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-medium">
              {formError}
            </div>
          )} */}

          {state.postType && (
            <PostForm
              postType={state.postType}
              title={state.title}
              content={state.content}
              selectedFont={state.selectedFont}
              selectedColor={state.selectedColor}
              backgroundOption={state.backgroundOption}
              tags={state.tags}
              currentTag={state.currentTag}
              currentChapter={state.currentChapter}
              chapterTitles={state.chapterTitles}
              chapterContents={state.chapterContents}
              currentScene={state.currentScene}
              sceneTitles={state.sceneTitles}
              sceneContents={state.sceneContents}
              setTitle={actions.setTitle}
              setContent={actions.setContent}
              setSelectedFont={actions.setSelectedFont}
              setSelectedColor={actions.setSelectedColor}
              setBackgroundOption={actions.setBackgroundOption}
              setCurrentTag={actions.setCurrentTag}
              setCurrentChapter={actions.setCurrentChapter}
              setChapterTitle={actions.setChapterTitle}
              setChapterContent={actions.setChapterContent}
              setCurrentScene={actions.setCurrentScene}
              setSceneTitle={actions.setSceneTitle}
              setSceneContent={actions.setSceneContent}
              handleTagKeyDown={helpers.handleTagKeyDown}
              handleContentKeyDown={helpers.handleContentKeyDown}
              getCurrentLineInfo={helpers.getCurrentLineInfo}
              removeTag={actions.removeTag}
              contentRef={contentRef}
            />
          )}
        </div>
      </div>

      {!state.postType && (
        <button
          onClick={() => setShowDraftModal(true)}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[85%] sm:w-auto sm:left-auto sm:translate-x-0 sm:right-8 px-5 py-3 rounded-full text-sm font-semibold bg-black text-white shadow-lg hover:bg-gray-800 active:scale-95 transition-all z-40 text-center"
        >
          My Drafts
        </button>
      )}

      <DraftModal
        isOpen={showDraftModal}
        onClose={() => setShowDraftModal(false)}
        onSelectDraft={handleSelectDraftForEdit}
      />

      <DraftActionModal
        isOpen={showDraftActionModal}
        onClose={() => setShowDraftActionModal(false)}
        onUpdateDraft={handleUpdateExistingDraft}
        onShareNow={() => {
          setShowDraftActionModal(false);
          handleShareNow();
        }}
        loading={state.loading}
      />

      <ErrorModal
        isOpen={!!formError}
        message={formError}
        onClose={() => setFormError("")}
      />
    </div>
  );
};

export default CreatePost;